import { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, addDays } from "date-fns";
import { CalendarIcon, Plus, X, Trash2 } from "lucide-react";
import { Client, PriceQuote, insertPriceQuoteSchema } from "@shared/schema";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent } from "@/components/ui/card";
import { formatCurrency } from "@/lib/currency";

// Define the schema for quote items
const quoteItemSchema = z.object({
  name: z.string().min(1, "Názov položky je povinný"),
  quantity: z.number().min(0.01, "Množstvo musí byť väčšie ako 0"),
  unitPrice: z.number().min(0, "Cena za jednotku musí byť 0 alebo väčšia"),
});

type QuoteItem = z.infer<typeof quoteItemSchema>;

// Extend the standard price quote schema with additional validations
const priceQuoteFormSchema = z.object({
  clientId: z.number({
    required_error: "Klient je povinný",
  }).optional(),
  title: z.string().min(1, "Názov ponuky je povinný"),
  description: z.string().optional(),
  amount: z.number().min(0, "Suma musí byť kladné číslo").optional(),
  totalAmount: z.number().min(0, "Celková suma musí byť kladné číslo").optional(),
  vatRate: z.number().min(0, "Sadzba DPH musí byť kladné číslo").optional(),
  validUntil: z.date().optional(),
  notes: z.string().optional(),
  paymentTerms: z.string().optional(),
  status: z.enum(['draft', 'sent', 'accepted', 'rejected', 'expired']).optional(),
  items: z.array(quoteItemSchema).optional(),
});

// Type for form data
type PriceQuoteFormData = z.infer<typeof priceQuoteFormSchema> & {
  items: QuoteItem[];
};

// Props for the form component
interface PriceQuoteFormProps {
  onSubmit: (data: PriceQuoteFormData) => void;
  defaultValues?: Partial<PriceQuote>;
  clients: Client[];
  isSubmitting?: boolean;
  onCancel?: () => void;
}

export function PriceQuoteForm({
  onSubmit,
  defaultValues,
  clients,
  isSubmitting = false,
  onCancel,
}: PriceQuoteFormProps) {
  const { t } = useTranslation(['priceQuotes', 'common']);
  const { user } = useAuth();
  const [calculatedVatAmount, setCalculatedVatAmount] = useState<number>(0);
  const [calculatedTotalAmount, setCalculatedTotalAmount] = useState<number>(0);

  // Get default VAT rate from user profile
  const defaultVatRate = user?.companyVatRate ? Number(user.companyVatRate) : 20;

  // Get default values for the form
  const getDefaultValues = (): Partial<PriceQuoteFormData> => {
    return {
      clientId: defaultValues?.clientId || undefined,
      title: defaultValues?.title || "",
      description: defaultValues?.description || "",
      amount: defaultValues?.amount ? Number(defaultValues.amount) : 0,
      totalAmount: defaultValues?.totalAmount ? Number(defaultValues.totalAmount) : 0,
      vatRate: defaultValues?.vatRate ? Number(defaultValues.vatRate) : defaultVatRate,
      validUntil: defaultValues?.validUntil ? new Date(defaultValues.validUntil) : addDays(new Date(), 30),
      notes: defaultValues?.notes || "",
      paymentTerms: defaultValues?.paymentTerms || t('defaultPaymentTerms'),
      status: defaultValues?.status || "draft",
      items: defaultValues?.items && defaultValues.items.length > 0 
        ? defaultValues.items.map(item => ({
            name: item.name || "",
            quantity: Number(item.quantity) || 1,
            unitPrice: Number(item.unitPrice) || 0
          }))
        : [{ name: "", quantity: 1, unitPrice: 0 }] // Default initial empty item
    };
  };

  const form = useForm<PriceQuoteFormData>({
    resolver: zodResolver(priceQuoteFormSchema),
    defaultValues: getDefaultValues(),
  });

  // Setup field array for managing quote items
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "items",
  });

  // Calculate totals from items
  const calculateTotals = () => {
    const items = form.getValues("items") || [];
    const netAmount = items.reduce((sum, item) => {
      const lineTotal = (item.quantity || 0) * (item.unitPrice || 0);
      return sum + lineTotal;
    }, 0);
    
    const vatRateValue = form.getValues("vatRate") || 0;
    const vat = user?.isVatPayer ? netAmount * (vatRateValue / 100) : 0;
    const total = netAmount + vat;
    
    // Set both amount fields in the form
    form.setValue("amount", netAmount);
    form.setValue("totalAmount", total);
    
    // Update state for display purposes
    setCalculatedVatAmount(vat);
    setCalculatedTotalAmount(total);
    
    return { netAmount, vat, total };
  };

  // Recalculate totals when items or VAT rate changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name?.startsWith('items') || name === 'vatRate') {
        calculateTotals();
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form.watch]);

  // Initialize calculations
  useEffect(() => {
    calculateTotals();
  }, []);

  // Handle form submission
  const handleSubmit = (data: PriceQuoteFormData) => {
    console.log('[PriceQuoteForm] handleSubmit called with:', data);
    
    // Calculate final amounts before submission
    const { netAmount, vat, total } = calculateTotals();
    
    console.log('[PriceQuoteForm] Calculated amounts:', { netAmount, vat, total });
    
    // Transform data to match backend schema
    const transformedData = {
      clientId: data.clientId,
      title: data.title,
      description: data.description,
      amount: netAmount,
      totalAmount: total,
      vatRate: data.vatRate,
      validUntil: data.validUntil,
      notes: data.notes,
      paymentTerms: data.paymentTerms,
      status: data.status,
      items: data.items
    };
    
    console.log('[PriceQuoteForm] Transformed data:', transformedData);
    
    onSubmit(transformedData as any);
  };

  return (
    <Form {...form}>
      <form 
        onSubmit={(e) => {
          console.log('[PriceQuoteForm] Form onSubmit event triggered');
          console.log('[PriceQuoteForm] Form values:', form.getValues());
          console.log('[PriceQuoteForm] Form errors:', form.formState.errors);
          form.handleSubmit(handleSubmit)(e);
        }} 
        className="space-y-6"
      >
        {/* Client selection */}
        <FormField
          control={form.control}
          name="clientId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('client')}</FormLabel>
              <FormControl>
                <select 
                  value={field.value ? field.value.toString() : ""} 
                  onChange={(e) => {
                    const value = e.target.value ? parseInt(e.target.value, 10) : undefined;
                    field.onChange(value);
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">{t('selectClient')}</option>
                  {clients && clients.length > 0 && clients.map((client) => (
                    <option key={client.id} value={client.id.toString()}>
                      {client.businessName || client.name}
                    </option>
                  ))}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Price quote title */}
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('title')}</FormLabel>
              <FormControl>
                <Input placeholder={t('common:priceQuotes.titlePlaceholder') || t('priceQuotes:titlePlaceholder') || 'Názov cenovej ponuky'} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Description */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('description')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t('common:priceQuotes.descriptionPlaceholder') || t('priceQuotes:descriptionPlaceholder') || 'Detailný popis cenovej ponuky'}
                  {...field}
                  className="min-h-24"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* VAT Rate - only shown for VAT payers */}
        {user?.isVatPayer && (
          <FormField
            control={form.control}
            name="vatRate"
            render={({ field }) => (
              <FormItem className="max-w-xs">
                <FormLabel>{t('vatRate')} (%)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="20"
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value ? parseFloat(e.target.value) : 0;
                      field.onChange(value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        
        {/* Quote Items Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">{t('items')}</h3>
            <Button 
              type="button" 
              variant="outline" 
              size="sm"
              onClick={() => append({ name: "", quantity: 1, unitPrice: 0 })}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('priceQuotes:addItem') || t('common:addItem')}
            </Button>
          </div>
          
          <Card>
            <CardContent className="p-4">
              {fields.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  {t('noItems')}
                </div>
              ) : (
                <div className="space-y-4">
                  {fields.map((field, index) => (
                    <div key={field.id} className="p-4 border rounded-md relative">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8"
                        onClick={() => remove(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name={`items.${index}.name`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('common:itemName')}</FormLabel>
                              <FormControl>
                                <Input placeholder={t('common:priceQuotes.itemNamePlaceholder') || t('priceQuotes:itemNamePlaceholder') || 'Zadajte názov položky'} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name={`items.${index}.quantity`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('common:quantity')}</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  placeholder="1"
                                  {...field}
                                  onChange={(e) => {
                                    const value = e.target.value ? parseFloat(e.target.value) : 0;
                                    field.onChange(value);
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name={`items.${index}.unitPrice`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('common:unitPrice')}</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  placeholder="0.00"
                                  {...field}
                                  onChange={(e) => {
                                    const value = e.target.value ? parseFloat(e.target.value) : 0;
                                    field.onChange(value);
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <div className="mt-2 text-right text-sm font-medium">
                        {t('common:lineTotal')}: {formatCurrency((form.watch(`items.${index}.quantity`) || 0) * (form.watch(`items.${index}.unitPrice`) || 0))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Calculated VAT and Total - different display for VAT vs non-VAT payers */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-md dark:bg-gray-800">
          {user?.isVatPayer ? (
            <>
              <div>
                <label className="text-sm font-medium">{t('common:vatAmount')}</label>
                <p className="font-semibold">
                  {formatCurrency(calculatedVatAmount)}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium">{t('common:totalWithVat')}</label>
                <p className="font-semibold">
                  {formatCurrency(calculatedTotalAmount)}
                </p>
              </div>
            </>
          ) : (
            <div>
              <label className="text-sm font-medium">{t('common:total')}</label>
              <p className="font-semibold">
                {formatCurrency(calculatedTotalAmount)}
              </p>
            </div>
          )}
        </div>

        {/* Valid until date */}
        <FormField
          control={form.control}
          name="validUntil"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('validUntil')}</FormLabel>
                  <FormControl>
                <Input
                  type="date"
                  value={field.value ? format(field.value, "yyyy-MM-dd") : ""}
                  onChange={(e) => {
                    const date = e.target.value ? new Date(e.target.value) : null;
                    field.onChange(date);
                  }}
                  min={format(new Date(), "yyyy-MM-dd")}
                  className="w-full"
                />
                  </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Payment terms */}
        <FormField
          control={form.control}
          name="paymentTerms"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('paymentTerms')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t('priceQuotes:paymentTermsPlaceholder')}
                  {...field}
                  className="min-h-20"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Notes */}
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('notes')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t('priceQuotes:notesPlaceholder')}
                  {...field}
                  className="min-h-20"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              {t('common:cancel')}
            </Button>
          )}
          <Button 
            type="submit" 
            disabled={isSubmitting}
            onClick={(e) => {
              console.log('[PriceQuoteForm] Submit button clicked');
              console.log('[PriceQuoteForm] Current form state:', form.formState);
              console.log('[PriceQuoteForm] Is valid:', form.formState.isValid);
            }}
          >
            {isSubmitting
              ? defaultValues
                ? t('updating')
                : t('creating')
              : defaultValues
              ? t('update')
              : t('create')}
          </Button>
        </div>
      </form>
    </Form>
  );
}